/**
 * Live chat transcripts for the trace reading panel, by trace.
 *
 * Session-scoped and deliberately not persisted: a panel conversation is
 * scratch thinking about what is on screen, and a reader returning tomorrow
 * wants the trace, not yesterday's questions. What it DOES have to survive is
 * the panel closing and reopening, and a selection change that remounts the
 * section around it - the thread is the one thing in there the reader made.
 *
 * A thread is two things held together: the items the reader sees, and the
 * model's own messages, so the agent rebuilt on a return answers a follow-up
 * with the conversation it belongs to.
 *
 * Bounded, because a long session can visit a lot of traces and every thread
 * holds its own tool results.
 *
 * A thread also holds the notes its answers pinned on the lanes
 * (`trace_annotate`): they are the thread's, so they live and go with it, and
 * the lanes view subscribes to them per trace.
 */

const threads = new Map();

export const THREAD_CAP = 12;

// Re-inserting on every write is what makes Map order the recency order.
function hold(traceId, patch) {
  if (!traceId) return;
  const held = threads.get(traceId) || { items: [], messages: [] };
  threads.delete(traceId);
  threads.set(traceId, { ...held, ...patch });
  while (threads.size > THREAD_CAP) {
    const oldest = threads.keys().next().value;
    threads.delete(oldest);
    notifyNotes(oldest);
  }
}

/** Hold one trace's items, evicting the least recently held once past the cap. */
export function rememberThread(traceId, items) {
  hold(traceId, { items });
}

export function readThread(traceId) {
  return threads.get(traceId)?.items || [];
}

/** Hold the model's messages for one trace's thread, beside its items. */
export function rememberThreadMessages(traceId, messages) {
  hold(traceId, { messages });
}

export function readThreadMessages(traceId) {
  return threads.get(traceId)?.messages || [];
}

export function heldThreadIds() {
  return [...threads.keys()];
}

export function clearThreads() {
  const ids = [...threads.keys()];
  threads.clear();
  for (const id of ids) notifyNotes(id);
}

// --- notes the thread's answers pinned on the lanes ---------------------------

// A thread's notes past this are dropped oldest first: pins are for the turns
// an answer points at, and a hundred of them would bury the lanes.
export const NOTE_CAP = 120;
const EMPTY_NOTES = Object.freeze([]);
const noteListeners = new Map();

function notifyNotes(traceId) {
  for (const cb of noteListeners.get(traceId) || []) {
    try { cb(); } catch { /* a listener must not break the chat */ }
  }
}

let noteSeq = 0;

/**
 * Pin notes on one trace's lanes: [{ printed, lane, laneLabel, text, question,
 * at }], `printed` being the turn number the axis prints. Returns the notes as
 * held, each with its id.
 */
export function addThreadNotes(traceId, notes) {
  if (!traceId || !notes?.length) return [];
  const added = notes.map((n) => ({ ...n, id: `n${Date.now().toString(36)}${(noteSeq += 1).toString(36)}` }));
  const held = threads.get(traceId)?.notes || [];
  hold(traceId, { notes: [...held, ...added].slice(-NOTE_CAP) });
  notifyNotes(traceId);
  return added;
}

export function readThreadNotes(traceId) {
  return threads.get(traceId)?.notes || EMPTY_NOTES;
}

export function clearThreadNotes(traceId) {
  if (!threads.get(traceId)?.notes?.length) return;
  hold(traceId, { notes: [] });
  notifyNotes(traceId);
}

export function subscribeThreadNotes(traceId, cb) {
  if (!noteListeners.has(traceId)) noteListeners.set(traceId, new Set());
  noteListeners.get(traceId).add(cb);
  return () => {
    const set = noteListeners.get(traceId);
    set?.delete(cb);
    if (set && !set.size) noteListeners.delete(traceId);
  };
}

// The reader chat's `ask` for a trace, registered while its panel is mounted,
// so a note's card can put a follow-up to the conversation that pinned it.
const askers = new Map();

export function registerTraceAsk(traceId, ask) {
  if (!traceId || typeof ask !== "function") return () => {};
  askers.set(traceId, ask);
  return () => { if (askers.get(traceId) === ask) askers.delete(traceId); };
}

/**
 * Put a question to a trace's reader chat, as if the reader typed it there:
 * `{ text, words, noteIds }`, `text` being what the chat is sent and `words`
 * what the reader wrote. The chat names the reply it queued; null when no
 * reader chat is open for the trace.
 */
export function askTrace(traceId, question) {
  const ask = askers.get(traceId);
  return ask ? ask(question) : null;
}
